import React from 'react';

// Ventures background: blurred triangle meshes that assemble into portions of
// shapes, hold, then come apart and drift away.  Drawn on a half-resolution
// canvas that is scaled up and blurred, which keeps the raster cost low.

const RENDER_SCALE = 0.5;
const FRAME_SKIP = 2;                 // ~30fps
const CLUSTER_COUNT = 4;
const SPACING = 26;                   // lattice spacing in CSS px

const COLORS = [
  { r: 0, g: 255, b: 0 },
  { r: 0, g: 200, b: 120 },
  { r: 120, g: 255, b: 160 },
  { r: 200, g: 255, b: 200 }
];

// Shape portions, each a test of whether a lattice point (relative to the
// cluster centre) belongs to the shape.  R is the cluster radius.
const SHAPES = [
  // Circle sector
  (x, y, R, p) => {
    const d = Math.hypot(x, y);
    const a = Math.atan2(y, x);
    const da = Math.atan2(Math.sin(a - p.a0), Math.cos(a - p.a0));
    return d <= R && Math.abs(da) <= p.span / 2;
  },
  // Ring segment
  (x, y, R, p) => {
    const d = Math.hypot(x, y);
    const a = Math.atan2(y, x);
    const da = Math.atan2(Math.sin(a - p.a0), Math.cos(a - p.a0));
    return d <= R && d >= R * 0.55 && Math.abs(da) <= p.span / 2;
  },
  // Half hexagon
  (x, y, R, p) => {
    const c = Math.cos(p.a0), s = Math.sin(p.a0);
    const rx = x * c + y * s, ry = -x * s + y * c;
    const ax = Math.abs(rx), ay = Math.abs(ry);
    const inHex = ay <= R * 0.866 && (ax * 0.866 + ay * 0.5) <= R * 0.866;
    return inHex && rx >= -R * 0.08;
  },
  // Slab (rotated rectangle)
  (x, y, R, p) => {
    const c = Math.cos(p.a0), s = Math.sin(p.a0);
    const rx = x * c + y * s, ry = -x * s + y * c;
    return Math.abs(rx) <= R && Math.abs(ry) <= R * 0.32;
  },
  // Triangle wedge
  (x, y, R, p) => {
    const c = Math.cos(p.a0), s = Math.sin(p.a0);
    const rx = x * c + y * s, ry = -x * s + y * c;
    return rx >= -R * 0.4 && rx <= R && Math.abs(ry) <= (R - rx) * 0.6;
  }
];

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

class VenturesBackground extends React.Component {
  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
    this.clusters = [];
    this.frame = 0;
    this.animationFrameId = null;
    this.handleResize = this.handleResize.bind(this);
  }

  componentDidMount() {
    this.handleResize();
    for (let i = 0; i < CLUSTER_COUNT; i++) {
      this.clusters.push(this.makeCluster(i * 0.25));
    }
    window.addEventListener('resize', this.handleResize);
    this.animate();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
  }

  handleResize() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    this.width = window.innerWidth || 1280;
    this.height = window.innerHeight || 800;
    canvas.width = Math.floor(this.width * RENDER_SCALE);
    canvas.height = Math.floor(this.height * RENDER_SCALE);
  }

  // Build a cluster: a hex lattice clipped to a shape portion, with
  // scattered start/end positions for each vertex.
  makeCluster(startPhase) {
    const R = 110 + Math.random() * 120;
    const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
    const params = { a0: Math.random() * Math.PI * 2, span: Math.PI * (0.5 + Math.random() * 0.8) };
    const cx = R + Math.random() * Math.max(1, this.width - 2 * R);
    const cy = R + Math.random() * Math.max(1, this.height - 2 * R);

    const rowH = SPACING * 0.866;
    const rows = Math.ceil(R / rowH) + 1;
    const cols = Math.ceil(R / SPACING) + 1;
    const index = new Map();
    const verts = [];

    for (let r = -rows; r <= rows; r++) {
      const xoff = (r & 1) ? SPACING / 2 : 0;
      for (let c = -cols; c <= cols; c++) {
        const x = c * SPACING + xoff;
        const y = r * rowH;
        if (!shape(x, y, R, params)) continue;
        const scatter = R * (1.4 + Math.random() * 1.2);
        const ang = Math.random() * Math.PI * 2;
        const ang2 = Math.random() * Math.PI * 2;
        index.set(`${r},${c}`, verts.length);
        verts.push({
          tx: x, ty: y,
          sx: Math.cos(ang) * scatter, sy: Math.sin(ang) * scatter,
          ex: Math.cos(ang2) * scatter, ey: Math.sin(ang2) * scatter,
          delay: Math.random() * 0.35,
          x: 0, y: 0
        });
      }
    }

    const tris = [];
    const at = (r, c) => index.get(`${r},${c}`);
    for (let r = -rows; r < rows; r++) {
      const odd = r & 1;
      for (let c = -cols; c < cols; c++) {
        // Neighbours in the row below depend on row parity
        const a = at(r, c), b = at(r, c + 1);
        const d = at(r + 1, odd ? c : c - 1), e = at(r + 1, odd ? c + 1 : c);
        if (a !== undefined && b !== undefined && e !== undefined) tris.push([a, b, e]);
        if (a !== undefined && e !== undefined && d !== undefined) tris.push([a, e, d]);
      }
    }

    if (verts.length < 6) return this.makeCluster(startPhase);

    return {
      cx, cy, verts, tris,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      drift: { x: (Math.random() - 0.5) * 0.15, y: (Math.random() - 0.5) * 0.15 },
      // Phase timings in seconds
      tAssemble: 3.5 + Math.random() * 2,
      tHold: 4 + Math.random() * 4,
      tDissolve: 3 + Math.random() * 2,
      phase: startPhase,               // 0..1 across the whole lifecycle
      rotation: (Math.random() - 0.5) * 0.0008
    };
  }

  stepCluster(cl, dt) {
    const total = cl.tAssemble + cl.tHold + cl.tDissolve;
    cl.phase += dt / total;
    cl.cx += cl.drift.x;
    cl.cy += cl.drift.y;
    if (cl.phase >= 1) return false;

    const t = cl.phase * total;
    for (const v of cl.verts) {
      let px, py, alpha;
      if (t < cl.tAssemble) {
        // Assemble: each vertex starts after its own delay
        const u = Math.max(0, Math.min(1, (t / cl.tAssemble - v.delay) / (1 - v.delay)));
        const k = easeInOut(u);
        px = v.sx + (v.tx - v.sx) * k;
        py = v.sy + (v.ty - v.sy) * k;
        alpha = k;
      } else if (t < cl.tAssemble + cl.tHold) {
        // Hold: subtle breathing
        const h = t - cl.tAssemble;
        const wob = Math.sin(h * 1.3 + v.tx * 0.05) * 1.5;
        px = v.tx + wob;
        py = v.ty + Math.cos(h * 1.1 + v.ty * 0.05) * 1.5;
        alpha = 1;
      } else {
        // Dissolve: vertices leave in their own order
        const u = Math.max(0, Math.min(1, ((t - cl.tAssemble - cl.tHold) / cl.tDissolve - v.delay) / (1 - v.delay)));
        const k = easeInOut(u);
        px = v.tx + (v.ex - v.tx) * k;
        py = v.ty + (v.ey - v.ty) * k;
        alpha = 1 - k;
      }
      v.x = px; v.y = py; v.alpha = alpha;
    }
    return true;
  }

  draw() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(RENDER_SCALE, 0, 0, RENDER_SCALE, 0, 0);
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, this.width, this.height);

    for (const cl of this.clusters) {
      const { r, g, b } = cl.color;
      const vs = cl.verts;
      ctx.save();
      ctx.translate(cl.cx, cl.cy);
      ctx.lineWidth = 1;
      for (const [i, j, k] of cl.tris) {
        const a = vs[i], bb = vs[j], c = vs[k];
        const alpha = Math.min(a.alpha, bb.alpha, c.alpha);
        if (alpha <= 0.01) continue;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(bb.x, bb.y);
        ctx.lineTo(c.x, c.y);
        ctx.closePath();
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha * 0.06})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(${r},${g},${b},${alpha * 0.45})`;
        ctx.stroke();
      }
      // Vertex points
      ctx.fillStyle = `rgba(${r},${g},${b},0.8)`;
      for (const v of vs) {
        if (v.alpha <= 0.01) continue;
        ctx.globalAlpha = v.alpha;
        ctx.fillRect(v.x - 1, v.y - 1, 2, 2);
      }
      ctx.globalAlpha = 1;
      ctx.restore();
    }
  }

  animate() {
    const loop = (now) => {
      this.frame++;
      if (this.frame % FRAME_SKIP === 0) {
        const dt = this.last ? Math.min(0.1, (now - this.last) / 1000) : 0.016;
        this.last = now;
        this.clusters = this.clusters.map(cl => (this.stepCluster(cl, dt) ? cl : this.makeCluster(0)));
        this.draw();
      }
      this.animationFrameId = requestAnimationFrame(loop);
    };
    this.animationFrameId = requestAnimationFrame(loop);
  }

  render() {
    return (
      <div className="ventures-background">
        <canvas ref={this.canvasRef} className="ventures-canvas" />
      </div>
    );
  }
}

export default VenturesBackground;
